import React, { useState, useEffect } from 'react';
import {
    Save,
    XCircle,
    CheckCircle,
    AlertTriangle,
    HardDrive,
    MapPin,
    Tag,
    Box,
    RadioTower,
    RefreshCw,
    Wifi,
    DoorOpen,
    Bluetooth,
    QrCode,
    Cpu,
} from 'lucide-react';
import * as deviceApi from '../services/deviceApi';
import LocationPickerMap from './LocationPickerMap';
import DeviceConnectionFields from './DeviceConnectionFields';
import { DEVICE_CATALOG, BRAND_LABELS, BRAND_META } from './deviceCatalog';

const parseCardDataFormat = (val) => {
    if (val === 2 || val === '2') return 2;
    const s = String(val || '').trim().toLowerCase();
    if (s.startsWith('hex')) return 2;
    return 1;
};

const parseCardTechnology = (val) => {
    if (val === 1 || val === '1') return 1;
    const s = String(val || '').trim().toLowerCase();
    if (s.includes('prox') || s === 'em') return 1;
    return 2;
};

const parsePhysicalType = (val) => {
    if (val === 2 || val === '2') return 2;
    const s = String(val || '').trim().toLowerCase();
    if (s.includes('turnstile') || s.includes('turnike')) return 2;
    return 1;
};

const parseWiegandBits = (val) => {
    const n = Number(String(val ?? '').replace(/\D+/g, ''));
    if ([26, 34, 35, 66].includes(n)) return n;
    return 34;
};

const initialFormState = {
    deviceName: '',
    location: '',
    serialNumber: '',
    brand: 'Hikvision',
    model: 'DS-K1T804BMF',
    isEntryDevice: true,
    isExitDevice: false,
    isQrDevice: false,
    isActive: true,
    isLocationBased: false,
    latitude: '',
    longitude: '',
    locationRadiusKm: '',
    relayDurationSeconds: 3,
    cardDataFormat: 1,      // 1 = Decimal, 2 = Hexadecimal
    cardTechnology: 2,      // 2 = Mifare, 1 = EM
    wiegandBitLength: 34,   // 26 | 34 | 35 | 66
    sourceName: '',
    bleSessionKey: '',
    isBleEnabled: true,
    physicalType: 1,        // 1 = Kapı (Door), 2 = Turnike (Turnstile)
    macAddress: '',
    bleName: '',
};

const initialConnState = {
    ipAddress: '',
    port: 80,
    useHttps: false,
    username: 'admin',
    password: '',
    protocol: 'ISAPI',
    sdkPort: 8000,
    verifyCertificate: false,
};

function DeviceForm({ device, onSave, onSubmit, onCancel }) {
    const isEditing = Boolean(device && device.id);
    const saveHandler = onSave || onSubmit;

    const [formData, setFormData] = useState(initialFormState);
    const [conn, setConn] = useState(initialConnState);
    const [customModel, setCustomModel] = useState('');

    const [testing, setTesting] = useState(false);
    const [fetchingInfo, setFetchingInfo] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const [gatewaySources, setGatewaySources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(false);

    const [readerLoading, setReaderLoading] = useState(false);
    const [readerMsg, setReaderMsg] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (device) {
            const isQr = Boolean(device.isQrDevice || device.brand === 'QR');
            const resolvedBrand = isQr ? 'QR' : (device.brand || 'Hikvision');
            const catalogModels = DEVICE_CATALOG[resolvedBrand] || [];
            const devModel = device.model || '';
            const isKnownModel = catalogModels.includes(devModel);

            setFormData({
                deviceName: device.deviceName || '',
                location: device.location || '',
                serialNumber: device.serialNumber || '',
                brand: resolvedBrand,
                model: isQr
                    ? (device.isLocationBased ? 'QR-Konumlu' : 'QR-Konumsuz')
                    : (isKnownModel ? devModel : (devModel ? 'Diğer' : (catalogModels[0] || ''))),
                isEntryDevice: Boolean(device.isEntryDevice),
                isExitDevice: Boolean(device.isExitDevice),
                isQrDevice: isQr,
                isActive: device.isActive !== undefined ? Boolean(device.isActive) : true,
                isLocationBased: Boolean(device.isLocationBased),
                latitude: device.latitude ?? '',
                longitude: device.longitude ?? '',
                locationRadiusKm: device.locationRadiusKm ?? '',
                relayDurationSeconds: device.relayDurationSeconds ?? 3,
                cardDataFormat: parseCardDataFormat(device.cardDataFormat),
                cardTechnology: parseCardTechnology(device.cardTechnology),
                wiegandBitLength: parseWiegandBits(device.wiegandBitLength),
                sourceName: device.sourceName || '',
                bleSessionKey: device.bleSessionKey || '',
                isBleEnabled: device.isBleEnabled !== undefined ? Boolean(device.isBleEnabled) : true,
                physicalType: parsePhysicalType(device.physicalType),
                macAddress: device.macAddress || '',
                bleName: device.bleName || '',
            });

            if (!isKnownModel && devModel && !isQr) {
                setCustomModel(devModel);
            } else {
                setCustomModel('');
            }

            const meta = BRAND_META[resolvedBrand] || BRAND_META.Hikvision;
            setConn({
                ipAddress: device.ipAddress ?? '',
                port: device.port ?? meta.defaultPort,
                useHttps: Boolean(device.useHttps),
                username: device.username ?? (resolvedBrand === 'Hikvision' ? 'admin' : ''),
                password: '',
                protocol: device.protocol ?? meta.defaultProtocol,
                sdkPort: device.sdkPort ?? 8000,
                verifyCertificate: Boolean(device.verifyCertificate),
            });
        } else {
            setFormData(initialFormState);
            setConn(initialConnState);
            setCustomModel('');
        }
        setFeedback(null);
        setReaderMsg('');
    }, [device]);

    useEffect(() => {
        let alive = true;
        const brand = formData.brand;
        if (!brand || brand === 'QR' || formData.isQrDevice) {
            setGatewaySources([]);
            return;
        }
        (async () => {
            try {
                setLoadingSources(true);
                const list = await deviceApi.listGatewaySources(brand);
                if (!alive) return;
                const arr = Array.isArray(list) ? list : [];
                setGatewaySources(arr);

                if (!formData.sourceName && arr.length === 1) {
                    setFormData((prev) => ({ ...prev, sourceName: arr[0].sourceName }));
                }
            } catch {
                if (alive) setGatewaySources([]);
            } finally {
                if (alive) setLoadingSources(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [formData.brand, formData.isQrDevice]);

    const handleBrandChange = (newBrand) => {
        const isQr = newBrand === 'QR';
        const models = DEVICE_CATALOG[newBrand] || [];
        const firstModel = models[0] || '';
        const meta = BRAND_META[newBrand] || BRAND_META.Hikvision;

        setFeedback(null);
        setCustomModel('');

        setFormData((prev) => ({
            ...prev,
            brand: newBrand,
            model: firstModel,
            isQrDevice: isQr,
            isLocationBased: isQr ? prev.isLocationBased : false,
            sourceName: '',
        }));

        if (!isQr) {
            setConn((prev) => ({
                ...prev,
                port: meta.defaultPort,
                protocol: meta.defaultProtocol,
                useHttps: false,
                verifyCertificate: false,
                username: meta.requiresAuth ? (prev.username || 'admin') : '',
                password: '',
                sdkPort: meta.defaultSdkPort || 8000,
            }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleDirectionChange = (direction) => {
        setFormData((prev) => ({
            ...prev,
            isEntryDevice: direction === 'entry',
            isExitDevice: direction === 'exit',
        }));
    };

    const handleQrModeChange = (isLocationBased) => {
        setFormData((prev) => ({
            ...prev,
            isLocationBased,
            model: isLocationBased ? 'QR-Konumlu' : 'QR-Konumsuz',
            latitude: isLocationBased ? (prev.latitude || 39.925533) : '',
            longitude: isLocationBased ? (prev.longitude || 32.866287) : '',
            locationRadiusKm: isLocationBased ? (prev.locationRadiusKm || 0.1) : '',
        }));
    };

    const handleLocationSelect = (lat, lng) => {
        setFormData((prev) => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
        }));
    };

    const formatMacInput = (raw) => {
        const clean = (raw || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
        const pairs = clean.match(/.{1,2}/g) || [];
        return pairs.slice(0, 6).join(':');
    };

    const handleTestConnection = async () => {
        setFeedback(null);
        if (!conn.ipAddress?.trim()) {
            setFeedback({ type: 'warning', text: 'Test için önce Cihaz IP Adresini girin.' });
            return;
        }
        try {
            setTesting(true);
            const payload = {
                deviceId: device?.id || null,
                brand: formData.brand,
                model: formData.model === 'Diğer' ? customModel : formData.model,
                ipAddress: conn.ipAddress.trim(),
                port: Number(conn.port) || 80,
                useHttps: Boolean(conn.useHttps),
                username: conn.username?.trim() || null,
                password: conn.password || null,
                protocol: conn.protocol || 'ISAPI',
                sdkPort: Number(conn.sdkPort) || 8000,
                verifyCertificate: Boolean(conn.verifyCertificate),
            };

            const res = await deviceApi.testConnection(payload);
            const ok = Boolean(res?.reachable && res?.authenticated);

            if (res?.macAddress || res?.serialNumber || res?.model) {
                setFormData((prev) => ({
                    ...prev,
                    macAddress: res.macAddress || prev.macAddress,
                    serialNumber: prev.serialNumber || res.serialNumber || '',
                }));
            }

            setFeedback({
                type: ok ? 'success' : (res?.reachable ? 'warning' : 'danger'),
                text: res?.message || (ok ? 'Bağlantı ve kimlik doğrulama başarılı.' : 'Bağlantı testi başarısız.'),
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Bağlantı testi sırasında hata oluştu.',
            });
        } finally {
            setTesting(false);
        }
    };

    const handleFetchDeviceInfo = async () => {
        setFeedback(null);
        if (!conn.ipAddress?.trim()) {
            setFeedback({ type: 'warning', text: 'Cihaz bilgilerini çekmek için önce IP adresini girin.' });
            return;
        }
        if (!isEditing && !conn.password) {
            setFeedback({ type: 'warning', text: 'Yeni cihazdan bilgi çekmek için Cihaz Şifresini girin.' });
            return;
        }

        try {
            setFetchingInfo(true);
            const dto = {
                deviceId: device?.id || null,
                ipAddress: conn.ipAddress.trim(),
                port: Number(conn.port) || (conn.useHttps ? 443 : 80),
                useHttps: Boolean(conn.useHttps),
                username: conn.username?.trim() || 'admin',
                password: conn.password || null,
                protocol: conn.protocol || 'ISAPI',
                sdkPort: Number(conn.sdkPort) || 8000,
                verifyCertificate: Boolean(conn.verifyCertificate),
            };

            const info = await deviceApi.fetchDeviceInfo(dto);
            if (!info?.success) {
                setFeedback({
                    type: 'danger',
                    text: info?.message || 'Cihaz bilgileri alınamadı.',
                });
                return;
            }

            const catalogModels = DEVICE_CATALOG[formData.brand] || [];
            const fetchedModel = (info.model || '').trim();
            const isFetchedInCatalog = catalogModels.includes(fetchedModel);

            setFormData((prev) => ({
                ...prev,
                deviceName: prev.deviceName || info.deviceName || '',
                serialNumber: info.serialNumber || prev.serialNumber,
                macAddress: info.macAddress || prev.macAddress,
                model: fetchedModel
                    ? (isFetchedInCatalog ? fetchedModel : 'Diğer')
                    : prev.model,
                relayDurationSeconds: info.relayDurationSeconds ?? prev.relayDurationSeconds,
                wiegandBitLength: info.wiegandBitLength ? Number(info.wiegandBitLength) : prev.wiegandBitLength,
                cardTechnology: info.cardTechnology ? Number(info.cardTechnology) : prev.cardTechnology,
            }));

            if (fetchedModel && !isFetchedInCatalog) {
                setCustomModel(fetchedModel);
            }

            setFeedback({
                type: 'success',
                text: `${info.message || 'Cihaz bilgileri başarıyla çekildi.'}${info.serialNumber ? ` (Seri No: ${info.serialNumber})` : ''}`,
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Cihaz bilgisi çekilirken hata oluştu.',
            });
        } finally {
            setFetchingInfo(false);
        }
    };

    const handleApplyDoorSettings = async () => {
        if (!device?.id) return;
        setFeedback(null);
        try {
            const secs = Number(formData.relayDurationSeconds || 3);
            const res = await deviceApi.applyDoorSettings(device.id, secs);
            setFeedback({
                type: res?.success ? 'success' : 'warning',
                text: res?.message || 'Röle süresi komutu gönderildi.',
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Röle süresi yazılamadı.',
            });
        }
    };

    const handleOpenDoorTest = async () => {
        if (!device?.id) return;
        setFeedback(null);
        try {
            const res = await deviceApi.openDoorById(device.id, {
                password: conn.password || null,
                doorNo: 1,
                durationSeconds: Number(formData.relayDurationSeconds || 3),
            });
            setFeedback({
                type: res?.success ? 'success' : 'warning',
                text: res?.message || (res?.success ? 'Kapı açma komutu gönderildi.' : 'Kapı açılamadı.'),
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Kapı açma hatası.',
            });
        }
    };

    const handleReadReaderSettings = async () => {
        if (!device?.id) return;
        setReaderMsg('');
        try {
            setReaderLoading(true);
            const r = await deviceApi.getReaderSettings(device.id, {
                username: conn.username,
                password: conn.password || undefined,
                useHttps: conn.useHttps,
                port: conn.port,
                verifyCertificate: conn.verifyCertificate,
                protocol: conn.protocol,
            });
            setFormData((prev) => ({
                ...prev,
                wiegandBitLength: Number(r?.wiegandBitLength || prev.wiegandBitLength || 34),
                cardTechnology: Number(r?.cardTechnology || prev.cardTechnology || 2),
                cardDataFormat: Number(r?.cardDataFormat || prev.cardDataFormat || 1),
            }));
            setReaderMsg(`Okundu (${r?.source === 'device' ? 'Cihaz ISAPI' : r?.source === 'sdk' ? 'Cihaz SDK' : 'Veritabanı'}).`);
        } catch (err) {
            setReaderMsg(err?.response?.data?.message || 'Cihazdan kart ayarları okunamadı.');
        } finally {
            setReaderLoading(false);
        }
    };

    const handleWriteReaderSettings = async () => {
        if (!device?.id) return;
        setReaderMsg('');
        try {
            setReaderLoading(true);
            const res = await deviceApi.updateReaderSettings(device.id, {
                wiegandBitLength: Number(formData.wiegandBitLength),
                cardTechnology: Number(formData.cardTechnology),
                cardDataFormat: Number(formData.cardDataFormat),
            });
            setReaderMsg(res?.message || 'Kart okuyucu ayarları kaydedildi.');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = err?.response?.data?.message || 'Kart okuyucu ayarları yazılamadı.';
            setReaderMsg(detail ? `${msg} (${detail})` : msg);
        } finally {
            setReaderLoading(false);
        }
    };

    const validateForm = () => {
        if (!formData.deviceName.trim()) return 'Cihaz adı zorunludur.';

        if (formData.isQrDevice || formData.brand === 'QR') {
            if (formData.isLocationBased) {
                if (formData.latitude === '' || formData.longitude === '') {
                    return 'Konumlu QR cihazı için haritadan koordinat seçmelisiniz.';
                }
                if (!formData.locationRadiusKm || Number(formData.locationRadiusKm) <= 0) {
                    return 'Geçerli bir konum yarıçapı (km) girmelisiniz.';
                }
            }
            return null;
        }

        if (!formData.sourceName?.trim()) {
            return 'Gateway (Source Name) seçimi zorunludur.';
        }
        if (!formData.serialNumber?.trim()) {
            return 'Cihaz seri numarası zorunludur.';
        }

        const meta = BRAND_META[formData.brand] || BRAND_META.Hikvision;
        if (formData.brand === 'Hikvision' || formData.brand === 'Suprema') {
            if (!conn.ipAddress?.trim()) {
                return 'Cihaz IP adresi zorunludur.';
            }
        }
        if (meta.requiresAuth) {
            if (!conn.username?.trim()) {
                return 'Hikvision cihazlar için kullanıcı adı zorunludur.';
            }
            if (!isEditing && !conn.password?.trim()) {
                return 'Yeni Hikvision cihaz eklerken cihaz şifresi zorunludur.';
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validateForm();
        if (err) {
            setFeedback({ type: 'danger', text: err });
            return;
        }

        const isQr = Boolean(formData.isQrDevice || formData.brand === 'QR');
        const resolvedModel = isQr
            ? (formData.isLocationBased ? 'QR-Konumlu' : 'QR-Konumsuz')
            : (formData.model === 'Diğer' ? (customModel.trim() || 'Diğer') : formData.model);

        const dataToSave = {
            ...formData,
            brand: isQr ? 'QR' : formData.brand,
            model: resolvedModel,
            isQrDevice: isQr,
            serialNumber: isQr ? (formData.serialNumber || null) : formData.serialNumber.trim(),
            macAddress: isQr ? null : (formData.macAddress?.trim() || null),
            sourceName: isQr ? null : (formData.sourceName?.trim() || null),
            latitude: isQr && formData.isLocationBased ? parseFloat(formData.latitude) : null,
            longitude: isQr && formData.isLocationBased ? parseFloat(formData.longitude) : null,
            locationRadiusKm: isQr && formData.isLocationBased ? parseFloat(formData.locationRadiusKm) : null,
            ipAddress: isQr ? null : (conn.ipAddress?.trim() || null),
            port: isQr ? 80 : (Number(conn.port) || 80),
            useHttps: isQr ? false : Boolean(conn.useHttps),
            username: isQr ? null : (conn.username?.trim() || null),
            protocol: isQr ? 'ISAPI' : (conn.protocol || 'ISAPI'),
            sdkPort: isQr ? 8000 : (Number(conn.sdkPort) || 8000),
            verifyCertificate: isQr ? false : Boolean(conn.verifyCertificate),
            relayDurationSeconds: Number(formData.relayDurationSeconds || 3),
            cardDataFormat: Number(formData.cardDataFormat || 1),
            cardTechnology: Number(formData.cardTechnology || 2),
            wiegandBitLength: Number(formData.wiegandBitLength || 34),
            physicalType: Number(formData.physicalType || 1),
        };

        if (isEditing) {
            dataToSave.id = device.id;
            if (conn.password?.trim()) {
                dataToSave.password = conn.password;
                dataToSave.plainPassword = conn.password;
            }
        } else if (!isQr && conn.password?.trim()) {
            dataToSave.password = conn.password;
        }

        try {
            setSubmitting(true);
            await saveHandler?.(dataToSave);
        } catch (saveErr) {
            setFeedback({
                type: 'danger',
                text: saveErr?.response?.data?.message || saveErr?.message || 'Cihaz kaydedilirken bir hata oluştu.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const isQr = Boolean(formData.isQrDevice || formData.brand === 'QR');
    const isHik = formData.brand === 'Hikvision';
    const isXpass = formData.brand === 'Xpass';
    const brandMeta = BRAND_META[formData.brand] || BRAND_META.Hikvision;
    const availableModels = DEVICE_CATALOG[formData.brand] || [];

    const inputClass =
        'w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all';
    const labelClass = 'block text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1';
    const sectionClass =
        'p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 space-y-2.5';

    const feedbackStyles = {
        success: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200',
        danger: 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200',
        warning: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200',
        info: 'bg-sky-50 dark:bg-sky-950/40 border-sky-300 dark:border-sky-800 text-sky-800 dark:text-sky-200',
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3.5">
            {feedback && (
                <div
                    className={`px-3.5 py-2.5 rounded-xl border flex items-center justify-between gap-3 ${
                        feedbackStyles[feedback.type] || feedbackStyles.info
                    }`}
                >
                    <div className="flex items-center gap-2 text-xs font-semibold">
                        {feedback.type === 'success' ? (
                            <CheckCircle className="w-4 h-4 shrink-0" />
                        ) : feedback.type === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                        ) : (
                            <XCircle className="w-4 h-4 shrink-0" />
                        )}
                        <span>{feedback.text}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setFeedback(null)}
                        className="text-xs font-bold opacity-75 hover:opacity-100"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* 1. ÜST BAR: CİHAZ MARKASI / TÜRÜ SEÇİMİ + AKTİFLİK */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
                    {Object.keys(DEVICE_CATALOG).map((bKey) => {
                        const active = (isQr && bKey === 'QR') || (!isQr && formData.brand === bKey);
                        return (
                            <button
                                key={bKey}
                                type="button"
                                onClick={() => handleBrandChange(bKey)}
                                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${
                                    active
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-500/20'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                                }`}
                            >
                                {bKey === 'QR' ? <QrCode className="w-3.5 h-3.5" /> : <Cpu className="w-3.5 h-3.5" />}
                                <span>{BRAND_LABELS[bKey] || bKey}</span>
                            </button>
                        );
                    })}
                </div>

                {isEditing && (
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            name="isActive"
                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300 dark:border-gray-600"
                            checked={formData.isActive}
                            onChange={handleChange}
                        />
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Cihaz Aktif</span>
                    </label>
                )}
            </div>

            {/* 2. ANA İÇERİK: YATAY 2 SÜTUNLU KOMPAKT DÜZEN */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
                {/* SOL SÜTUN: TEMEL CİHAZ BİLGİLERİ & DONANIM / RÖLE */}
                <div className="space-y-3.5">
                    <div className={sectionClass}>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                            <HardDrive className="w-3.5 h-3.5" />
                            Temel Cihaz Bilgileri
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            <div>
                                <label className={labelClass}>
                                    Cihaz Adı <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="deviceName"
                                    className={inputClass}
                                    placeholder="Örn: Ana Giriş Turnike 1"
                                    value={formData.deviceName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div>
                                <label className={labelClass}>
                                    <MapPin className="inline w-3 h-3 mr-1 text-indigo-500" />
                                    Konum / Açıklama
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    className={inputClass}
                                    placeholder="Örn: İdari Bina Zemin Kat"
                                    value={formData.location}
                                    onChange={handleChange}
                                />
                            </div>

                            {!isQr && (
                                <>
                                    <div>
                                        <label className={labelClass}>
                                            <Box className="inline w-3 h-3 mr-1 text-indigo-500" />
                                            Model
                                        </label>
                                        <select
                                            name="model"
                                            className={inputClass}
                                            value={formData.model}
                                            onChange={handleChange}
                                        >
                                            {availableModels.map((m) => (
                                                <option key={m} value={m}>
                                                    {m}
                                                </option>
                                            ))}
                                        </select>
                                        {formData.model === 'Diğer' && (
                                            <input
                                                type="text"
                                                className={`${inputClass} mt-1.5`}
                                                placeholder="Model kodu (Örn: DS-K1T341BMI)"
                                                value={customModel}
                                                onChange={(e) => setCustomModel(e.target.value)}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className={labelClass}>
                                            <Tag className="inline w-3 h-3 mr-1 text-indigo-500" />
                                            Gateway Servisi <span className="text-rose-500">*</span>
                                        </label>
                                        <select
                                            name="sourceName"
                                            className={inputClass}
                                            value={formData.sourceName || ''}
                                            onChange={handleChange}
                                            disabled={loadingSources}
                                            required
                                        >
                                            <option value="">
                                                {loadingSources ? 'Yükleniyor...' : 'Gateway seçin...'}
                                            </option>
                                            {gatewaySources.map((x) => (
                                                <option key={`${x.serviceId}-${x.sourceName}`} value={x.sourceName}>
                                                    {x.sourceName} ({x.vendor})
                                                </option>
                                            ))}
                                            {formData.sourceName &&
                                                !gatewaySources.some((x) => x.sourceName === formData.sourceName) && (
                                                    <option value={formData.sourceName}>
                                                        {formData.sourceName} (Mevcut)
                                                    </option>
                                                )}
                                        </select>
                                        {gatewaySources.length === 0 && !loadingSources && (
                                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                                                Aktif {formData.brand} Gateway kaydı bulunamadı.
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* GEÇİŞ YÖNÜ & FİZİKSEL TİP (SEÇİMLİ BUTONLAR) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                            <div>
                                <label className={labelClass}>
                                    Geçiş Yönü <span className="text-rose-500">*</span>
                                </label>
                                <div className="flex p-0.5 rounded-lg bg-gray-200/80 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => handleDirectionChange('entry')}
                                        className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            formData.isEntryDevice
                                                ? 'bg-emerald-600 text-white shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Giriş Cihazı
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDirectionChange('exit')}
                                        className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            formData.isExitDevice
                                                ? 'bg-rose-600 text-white shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Çıkış Cihazı
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Fiziksel Tip</label>
                                <div className="flex p-0.5 rounded-lg bg-gray-200/80 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, physicalType: 1 }))}
                                        className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            Number(formData.physicalType) !== 2
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Kapı Okuyucusu
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, physicalType: 2 }))}
                                        className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            Number(formData.physicalType) === 2
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Turnike Okuyucusu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* DONANIM KİMLİĞİ & RÖLE SÜRESİ (Fiziksel cihazlar için) */}
                    {!isQr && (
                        <div className={sectionClass}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                    <Cpu className="w-3.5 h-3.5" />
                                    Donanım Kimliği &amp; Röle Kontrolü
                                </h4>

                                {isEditing && isHik && (
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={handleApplyDoorSettings}
                                            className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 transition-all"
                                        >
                                            Röle Süresini Yaz
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleOpenDoorTest}
                                            className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-all flex items-center gap-1"
                                        >
                                            <DoorOpen className="w-3 h-3" />
                                            Kapıyı Aç (Test)
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div>
                                    <label className={labelClass}>
                                        {isXpass ? 'MAC / Seri No' : 'Seri Numarası'}{' '}
                                        <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="serialNumber"
                                        className={`${inputClass} font-mono`}
                                        placeholder={isXpass ? 'AA:BB:CC:DD:EE:FF' : 'Seri No'}
                                        value={formData.serialNumber}
                                        onChange={(e) => {
                                            if (isXpass) {
                                                const formatted = formatMacInput(e.target.value);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    serialNumber: formatted,
                                                    macAddress: formatted,
                                                }));
                                            } else {
                                                handleChange(e);
                                            }
                                        }}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>MAC Adresi</label>
                                    <input
                                        type="text"
                                        name="macAddress"
                                        className={`${inputClass} font-mono`}
                                        placeholder="AA:BB:CC:DD:EE:FF"
                                        value={formData.macAddress || ''}
                                        onChange={(e) => {
                                            const formatted = formatMacInput(e.target.value);
                                            setFormData((prev) => ({
                                                ...prev,
                                                macAddress: formatted,
                                                ...(isXpass ? { serialNumber: formatted } : {}),
                                            }));
                                        }}
                                    />
                                </div>

                                {brandMeta.supportsRelaySettings && (
                                    <div>
                                        <label className={labelClass}>Röle Süresi (Sn)</label>
                                        <input
                                            type="number"
                                            className={inputClass}
                                            min={1}
                                            max={255}
                                            value={formData.relayDurationSeconds ?? 3}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    relayDurationSeconds: Math.max(
                                                        1,
                                                        Math.min(255, Number(e.target.value || 3))
                                                    ),
                                                }))
                                            }
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Sadece Orion (Xpass) için Bluetooth & BLE Ayarları */}
                            {isXpass && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2 border-t border-gray-200 dark:border-gray-700 items-end">
                                    <div className="pb-1.5">
                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="isBleEnabled"
                                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                                                checked={formData.isBleEnabled}
                                                onChange={handleChange}
                                            />
                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-1">
                                                <Bluetooth className="w-3.5 h-3.5 text-sky-500" />
                                                BLE Aktif
                                            </span>
                                        </label>
                                    </div>

                                    <div>
                                        <label className={labelClass}>BLE Yayın Adı</label>
                                        <input
                                            type="text"
                                            name="bleName"
                                            className={inputClass}
                                            placeholder="ORION_KAPI_1"
                                            value={formData.bleName || ''}
                                            onChange={handleChange}
                                            disabled={!formData.isBleEnabled}
                                        />
                                    </div>

                                    <div>
                                        <label className={labelClass}>BLE Anahtarı</label>
                                        <div className="flex gap-1.5">
                                            <input
                                                type="text"
                                                name="bleSessionKey"
                                                className={`${inputClass} font-mono`}
                                                placeholder="Otomatik"
                                                value={formData.bleSessionKey || ''}
                                                onChange={handleChange}
                                                disabled={!formData.isBleEnabled}
                                            />
                                            <button
                                                type="button"
                                                disabled={!formData.isBleEnabled}
                                                onClick={() => {
                                                    const randomHex = Array.from({ length: 32 }, () =>
                                                        Math.floor(Math.random() * 16).toString(16)
                                                    )
                                                        .join('')
                                                        .toUpperCase();
                                                    setFormData((prev) => ({ ...prev, bleSessionKey: randomHex }));
                                                }}
                                                className="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                                            >
                                                Üret
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* SAĞ SÜTUN: AĞ BAĞLANTISI & KART OKUYUCU (veya QR HARİTASI) */}
                <div className="space-y-3.5">
                    {isQr ? (
                        <div className={sectionClass}>
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                    <QrCode className="w-3.5 h-3.5" />
                                    Mobil QR Çalışma Modu
                                </h4>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    QR seri numarası kayıt sırasında otomatik üretilir.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                <div
                                    onClick={() => handleQrModeChange(false)}
                                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                                        !formData.isLocationBased
                                            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30 ring-1 ring-indigo-500'
                                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                    }`}
                                >
                                    <div className="font-bold text-xs text-gray-900 dark:text-gray-100">
                                        Konumsuz QR (Standart)
                                    </div>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                        GPS mesafe doğrulaması yapılmaz.
                                    </p>
                                </div>

                                <div
                                    onClick={() => handleQrModeChange(true)}
                                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                                        formData.isLocationBased
                                            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30 ring-1 ring-indigo-500'
                                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                    }`}
                                >
                                    <div className="font-bold text-xs text-gray-900 dark:text-gray-100">
                                        Konumlu QR (Mesafe Sınırlı)
                                    </div>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                        Seçilen yarıçap içinde okutma zorunludur.
                                    </p>
                                </div>
                            </div>

                            {formData.isLocationBased && (
                                <div className="space-y-2.5 pt-1">
                                    <LocationPickerMap
                                        lat={parseFloat(formData.latitude) || 39.925533}
                                        lng={parseFloat(formData.longitude) || 32.866287}
                                        radiusKm={parseFloat(formData.locationRadiusKm) || 0.1}
                                        onLocationChange={handleLocationSelect}
                                    />
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className={labelClass}>Enlem</label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="latitude"
                                                className={inputClass}
                                                value={formData.latitude}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Boylam</label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="longitude"
                                                className={inputClass}
                                                value={formData.longitude}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Yarıçap (Km)</label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="locationRadiusKm"
                                                className={inputClass}
                                                placeholder="0.15"
                                                value={formData.locationRadiusKm}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <DeviceConnectionFields
                                    value={conn}
                                    onChange={setConn}
                                    brand={formData.brand}
                                    isEditing={isEditing}
                                />

                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleTestConnection}
                                        disabled={testing}
                                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 transition-all flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                        <Wifi className="w-3.5 h-3.5" />
                                        {testing ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
                                    </button>

                                    {brandMeta.supportsIsapiFetch && (
                                        <button
                                            type="button"
                                            onClick={handleFetchDeviceInfo}
                                            disabled={fetchingInfo}
                                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-all flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${fetchingInfo ? 'animate-spin' : ''}`} />
                                            {fetchingInfo
                                                ? 'Çekiliyor...'
                                                : 'Cihaz Bilgilerini Çek (Seri No / MAC / Model)'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {brandMeta.supportsReaderSettings && (
                                <div className={sectionClass}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                            <RadioTower className="w-3.5 h-3.5" />
                                            Kart Okuyucu &amp; Wiegand Ayarları
                                        </h4>

                                        {isEditing && isHik && (
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={handleReadReaderSettings}
                                                    disabled={readerLoading}
                                                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
                                                >
                                                    {readerLoading ? 'Okunuyor...' : 'Cihazdan Oku'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleWriteReaderSettings}
                                                    disabled={readerLoading}
                                                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50"
                                                >
                                                    {readerLoading ? 'Yazılıyor...' : 'Cihaza & Sisteme Yaz'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                        <div>
                                            <label className={labelClass}>Kart Veri Formatı</label>
                                            <select
                                                className={inputClass}
                                                value={Number(formData.cardDataFormat || 1)}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        cardDataFormat: Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value={1}>Decimal (Onluk)</option>
                                                <option value={2}>Hexadecimal (Hex)</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className={labelClass}>Kart Teknolojisi</label>
                                            <select
                                                className={inputClass}
                                                value={Number(formData.cardTechnology || 2)}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        cardTechnology: Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value={2}>Mifare (13.56 MHz)</option>
                                                <option value={1}>EM / Proximity</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className={labelClass}>Wiegand Bit</label>
                                            <select
                                                className={inputClass}
                                                value={Number(formData.wiegandBitLength || 34)}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        wiegandBitLength: Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value={26}>Wiegand 26 Bit</option>
                                                <option value={34}>Wiegand 34 Bit</option>
                                                <option value={35}>Wiegand 35 Bit</option>
                                                <option value={66}>Wiegand 66 Bit</option>
                                            </select>
                                        </div>
                                    </div>

                                    {readerMsg && (
                                        <p className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">
                                            {readerMsg}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* FOOTER BUTONLARI */}
            <div className="flex justify-end items-center gap-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                >
                    İptal
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                >
                    <Save className="w-3.5 h-3.5" />
                    {submitting ? 'Kaydediliyor...' : isEditing ? 'Değişiklikleri Kaydet' : 'Cihazı Oluştur'}
                </button>
            </div>
        </form>
    );
}

export default DeviceForm;
